import React, { forwardRef } from 'react'

// MUI Imports
import { useTheme } from '@mui/material/styles'
import {
  Tooltip
} from '@mui/material'

const BarItem = forwardRef((props, ref) => {
  const { percent, percentLimit, theme, color, percentText = null, ...restProps } = props

  return (
    <div
      ref={ref}
      className='flex justify-center items-center h-full overflow-hidden relative transition-all duration-300 ease-in-out'
      style={{
        backgroundColor: color,
        width: `${percent}%`,
        fontSize: theme.typography.body2.fontSize
      }}
      {...restProps}
    >
      <span className='text-white font-semibold text-[10px] drop-shadow-sm'>
        {percentText || (percent >= percentLimit ? `${percent}%` : '')}
      </span>
    </div>
  )
})

const PercentageBar = ({ labelArr, valueArr, colorArr, percentLimit, emptyColor }) => {
  const theme = useTheme()

  const getPositioningClass = (cumulativePercent) => {
    const shouldRightAlign = cumulativePercent < 15
    const shouldLeftAlign = cumulativePercent > 85

    if (shouldRightAlign) return 'translate-x-0'
    if (shouldLeftAlign) return '-translate-x-full'
    return '-translate-x-1/2'
  }

  const processedLabels = valueArr?.map((value, index) => {
    const localPercent = Number(value) || 0
    let cumulativePercent = 0
    for (let i = 0; i <= index; i++) {
      cumulativePercent += Number(valueArr[i]) || 0
    }

    const positioningClass = getPositioningClass(cumulativePercent)

    return {
      localPercent,
      positioningClass,
      cumulativePercent,
      shouldShow: localPercent >= 10
    }
  }) || []

  return (
    <div className='w-full'>
      <div className='relative h-6 w-full mb-1'>
        {processedLabels.map((item, index) => {
          if (!item.shouldShow) return null

          return (
            <div key={index}>
              <div
                className={`absolute top-0 ${item.positioningClass}`}
                style={{ left: `${item.cumulativePercent}%` }}
              >
                <div
                  className='text-[10px] font-semibold px-1.5 py-0.5 rounded bg-white/90 border border-black/10 uppercase whitespace-nowrap shadow-sm text-gray-900'
                >
                  {labelArr[index]}
                </div>
              </div>
              <div
                className='absolute w-px h-1.5 rounded-[1px] -translate-x-1/2 top-[22px] bg-gradient-to-b from-gray-500/80 to-gray-500/30 shadow-sm'
                style={{
                  left: `${item.cumulativePercent}%`
                }}
              />
            </div>
          )
        })}
      </div>

      <div className='flex h-4 w-full rounded-md overflow-hidden bg-black/6'>
        {valueArr?.length
          ? valueArr.map((value, index) => {
            const localPercent = Number(value) || 0

            const color = colorArr[index]

            return (
              localPercent >= percentLimit
                ? <BarItem
                    key={index}
                    percent={localPercent}
                    percentLimit={percentLimit}
                    theme={theme}
                    color={color}
                  />
                : <Tooltip
                    key={index}
                    title={`${labelArr[index]} ${localPercent}%`}
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          color: 'white',
                          bgcolor: colorArr[index],
                          '& .MuiTooltip-arrow': {
                            color: colorArr[index]
                          }
                        }
                      }
                    }}
                  >
                    <BarItem
                      percent={localPercent}
                      percentLimit={percentLimit}
                      theme={theme}
                      color={color}
                    />
                  </Tooltip>
            )
          })
          : <BarItem
              percent={100}
              percentLimit={0}
              theme={theme}
              color={emptyColor}
              percentText={'0%'}
            />
        }
      </div>
    </div>
  )
}

export default PercentageBar
